import { useCallback, useEffect, useReducer, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { settlePreviewKey, useSettlePreview } from '@/lib/finance';
import {
  initialDraft,
  previewBankMinor,
  reduceDraft,
  sameContext,
  type Anchor,
  type DraftContext,
  type DraftEvent,
} from '@/lib/settleDraft';
import type { FinanceSettlePreviewRequest } from '@/api/generated/types.gen';

// Before the account, currency and date are known there is no context to ask
// about; the draft waits on this one, and the first real context resets it
// (C3 — nothing manual can be in it yet, the money inputs are disabled until then).
const NO_CONTEXT: DraftContext = { accountId: '', currency: '', occurredOn: '', targets: [] };

const sameOrder = (a: readonly string[], b: readonly string[]) => a.length === b.length && a.every((k, i) => k === b[i]);

function targetRef(k: DraftContext['targets'][number]): FinanceSettlePreviewRequest['targets'][number] {
  return k.startsWith('expected:') ? { expected_id: k.slice('expected:'.length) } : { schedule_entry_id: k.slice('schedule_entry:'.length) };
}

/**
 * The settle draft over the preview (OH-193 spec §4.4): the reducer from
 * lib/settleDraft plus the request that feeds it.
 *
 * One source for the request shape [PR3-01]: `initialSendsBank` seeds the
 * draft (batch true, single false) and from then on only
 * `dispatch({ type: 'requestShape' })` — sent by the form's remainder-mode
 * handler — changes it; the request is built from `state.sendsBank`, so no
 * render order decides which request goes out.
 *
 * The bank amount goes into the request only in that shape and only when
 * the operator typed what it comes from in this context (previewBankMinor:
 * manual, parsed, not stale) — a suggested value is the server's own number,
 * sending it back would only echo it. In the fee mode the request asks about
 * what arrived (OH-205 §6): counting from the payment amount that is the
 * payment field, from the bank amount the bank itself.
 *
 * `initialAnchor` (OH-201): the single forms count from the payment amount;
 * the batch from the bank amount, as before.
 *
 * `bankBeforeOperatorLines` (OH-202): counting from the bank, the single
 * forms ask the fee mode about the bank before the operator's own lines
 * (bankBeforeOperatorLinesMinor), so the suggested shortfall covers only
 * what those lines don't. The batch asks about the bank its draft holds,
 * which since OH-212 the window sets itself — `planned_total − fee`, the ask
 * for the fee the bank amount typed implies — not the amount typed.
 */
export function useSettleDraft(o: {
  context: DraftContext | null;
  initialSendsBank: boolean;
  initialAnchor?: Anchor;
  bankBeforeOperatorLines?: boolean;
}) {
  const { context, initialSendsBank, initialAnchor, bankBeforeOperatorLines = false } = o;
  const [state, dispatch] = useReducer(reduceDraft, null, () =>
    initialDraft(context ?? NO_CONTEXT, initialSendsBank, initialAnchor),
  );

  useEffect(() => {
    if (!context) return;
    if (sameContext(context, state.context) && sameOrder(context.targets, state.context.targets)) return;
    dispatch({ type: 'context', context });
  }, [context, state.context]);

  // Only for the context the draft is in now: a draft that has not caught up with the prop asks about nothing.
  const bankMinor = context && state.context.currency === context.currency ? previewBankMinor(state, bankBeforeOperatorLines) : null;
  const req: FinanceSettlePreviewRequest | null = context
    ? {
        account_id: context.accountId,
        currency: context.currency,
        occurred_on: context.occurredOn,
        targets: context.targets.map(targetRef),
        ...(bankMinor !== null && bankMinor > 0 ? { bank_amount_minor: bankMinor } : {}),
      }
    : null;
  const preview = useSettlePreview(req && context ? { req, context, sentBank: state.sendsBank } : null);

  // Only answers fetched while this draft exists: a cached answer from an
  // earlier mount (the same row reopened) would become the first fill and the
  // fresh one would then read as a conflict on an untouched draft.
  const [mountedAt] = useState(() => Date.now());
  const answer = preview.data && preview.dataUpdatedAt >= mountedAt ? preview.data : undefined;
  // Delivered again whenever the draft's context or request shape changes, not
  // only when a new answer arrives: an answer the reducer dropped (S1) becomes
  // applicable again after A→B→A or fee→partial→fee inside the debounce, when
  // the query key — and so `data` — never changed. S1 drops a non-matching
  // answer; S2/S3 are idempotent for a matching one.
  useEffect(() => {
    if (!answer) return;
    dispatch({ type: 'suggestion', response: answer.response, context: answer.context, sentBank: answer.sentBank });
  }, [answer, state.context, state.sendsBank]);

  const qc = useQueryClient();
  // 409 target_changed: ask again; the answer takes the draft into a conflict through S3 by itself
  const refetchAfterConflict = useCallback(() => {
    void qc.invalidateQueries({ queryKey: settlePreviewKey });
  }, [qc]);

  return { state, dispatch: dispatch as (e: DraftEvent) => void, preview, refetchAfterConflict };
}
